import { planCompression } from "../compressionPlan";
// Findings the CDT method cannot produce. Vocabulary is merchandising, not SpineLevel.orient.
import { CdtLayoutOptions, CdtPoolReason, buildFromCdt } from "./buildFromCdt";
import {
  PATH_SEPARATOR,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
  isPlaceholderValue,
} from "../studioTypes";

/** 4 ft in cm — the reach line (CDT-LAYOUT-SPEC §9). */
export const FT4_CM = 121.92;
const LB_PER_KG = 2.20462;

export type ExpressibilityClass =
  | "vertical-band"
  | "horizontal-band"
  | "thin-column"
  | "heavy-above-4ft";

export type Carrier =
  | { kind: "restriction-row" }
  | { kind: "mudmap"; node: "segment" | "block" }
  | { kind: "cdt-edit" }
  | { kind: "none" };

export interface ExpressibilityEvidence {
  baseShelves?: readonly number[];
  builtShelves?: readonly number[];
  perWindow?: readonly { path: string; base: readonly number[]; built: readonly number[] }[];
  notes: readonly string[];
}

export interface ExpressibilityFinding {
  id: string;
  kind: ExpressibilityClass;
  run: number;
  attr: string;
  value: string;
  skuIds: readonly string[];
  carrier: Carrier;
  /** (sibling value, shelf) pairs a row removes; CAN_HAVE is a hard allow-list,
   * so naming V on a shelf excludes every sibling there. */
  siblingEvictions: number;
  aliases: readonly { attr: string; value: string }[];
  ambiguous: boolean;
  detail: string;
  evidence: ExpressibilityEvidence;
}

export interface ExpressibilityOptions {
  layout?: CdtLayoutOptions;
  /** Share of a bay a band must hold before a row may name that bay. */
  bayFillFrac?: number;
  /** Share of the band's own x-extent that may belong to other values. */
  foreignFrac?: number;
  stackFillFrac?: number;
  heavyLb?: number;
}

export interface ExpressibilityReport {
  findings: readonly ExpressibilityFinding[];
  /** `tooNarrow` merges away; `tooFewSkus` can never be a column on this fixture. */
  thin: { tooNarrow: number; tooFewSkus: number };
  weightUnavailable: boolean;
}

const sortedUnique = (values: Iterable<number>): number[] =>
  [...new Set(values)].sort((a, b) => a - b);

/** Union length of `spans` clipped to [lo, hi] — OCCUPIED width, not extent. */
const coveredCm = (
  spans: readonly { lo: number; hi: number }[],
  lo: number,
  hi: number,
): number => {
  const clipped = spans
    .map((span) => ({ lo: Math.max(span.lo, lo), hi: Math.min(span.hi, hi) }))
    .filter((span) => span.hi > span.lo)
    .sort((a, b) => a.lo - b.lo);
  let total = 0;
  let cursor = lo;
  for (const span of clipped) {
    if (span.hi <= cursor) continue;
    total += span.hi - Math.max(span.lo, cursor);
    cursor = Math.max(cursor, span.hi);
  }
  return total;
};

const isContiguous = (shelves: readonly number[]): boolean =>
  shelves.every((shelf, index) => index === 0 || shelf === shelves[index - 1] + 1);

/**
 * collapse4 merges adjacent levels only, so collapsed depth is at most the live
 * index: 0 is cdt0, 1 a segment, 2 a block. Index 3+ may land at cdt3, no node.
 */
const carrierFor = (liveIndex: number): Carrier => {
  if (liveIndex === 0) return { kind: "restriction-row" };
  if (liveIndex === 1) return { kind: "mudmap", node: "segment" };
  if (liveIndex === 2) return { kind: "mudmap", node: "block" };
  return { kind: "none" };
};

interface Span {
  shelf: number;
  lo: number;
  hi: number;
}

export const expressibilityAudit = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  options: ExpressibilityOptions = {},
): ExpressibilityReport => {
  const empty: ExpressibilityReport = {
    findings: [],
    thin: { tooNarrow: 0, tooFewSkus: 0 },
    weightUnavailable: true,
  };
  if (items.length === 0 || runs.length === 0 || draft.spine.length === 0) return empty;

  const bayFillFrac = options.bayFillFrac ?? 0.9;
  const foreignFrac = options.foreignFrac ?? 0.1;
  // Asks only whether the space beneath the band is claimed, not that it is
  // packed: a shelf free for lack of product sits near zero.
  const stackFillFrac = options.stackFillFrac ?? 0.6;
  const heavyKg = (options.heavyLb ?? 10) / LB_PER_KG;
  const { layout, report } = buildFromCdt(items, runs, draft, options.layout ?? {});

  const roleOf = new Map(report.roles.map((role) => [role.level, role.role]));
  const liveIndexOf = new Map(
    planCompression(items, draft, runs).levels.flatMap((tier, index) =>
      tier.levels.map((level) => [level.attr, index] as const),
    ),
  );
  const attrsOf = new Map(items.map((item) => [item.skuId, effectiveAttrs(item, draft)]));
  const itemOf = new Map(items.map((item) => [item.skuId, item]));
  const valueOf = (skuId: string, attr: string): string => String(attrsOf.get(skuId)?.[attr] ?? "");

  const baseSpans = (skuId: string, run: number): Span[] =>
    (itemOf.get(skuId)?.placements ?? [])
      .filter((placement) => placement.run === run)
      .map((placement) => ({
        shelf: placement.shelf,
        lo: placement.xCm,
        hi: placement.xCm + placement.widthCm,
      }));

  /** Shelf is exact in a layout; x is only an anchor. */
  const builtShelves = (skuId: string): number[] =>
    (layout.placements[skuId]?.segments ?? []).map((segment) => segment.shelf);

  const findings: ExpressibilityFinding[] = [];

  for (const run of runs) {
    const shelfRanks = sortedUnique(run.shelves.map((shelf) => shelf.rank));
    if (shelfRanks.length === 0) continue;
    const inRun = items.filter((item) => item.run === run.run);
    if (inRun.length === 0) continue;

    // C1: a vertical level's values get full-height x-windows by construction,
    // so a value banded to shelves 1-2 is inexpressible at any value order.
    for (const [attr, role] of roleOf) {
      if (role !== "vertical") continue;
      const liveIndex = liveIndexOf.get(attr) ?? 0;

      const byValue = new Map<string, StudioItem[]>();
      for (const item of inRun) {
        const value = valueOf(item.skuId, attr);
        if (isPlaceholderValue(value)) continue;
        byValue.set(value, [...(byValue.get(value) ?? []), item]);
      }
      if (byValue.size < 2) continue;

      for (const [value, own] of byValue) {
        const mine = own.flatMap((item) => baseSpans(item.skuId, run.run));
        const base = sortedUnique(mine.map((span) => span.shelf));
        if (base.length === 0 || !isContiguous(base)) continue;
        const free = shelfRanks.filter((rank) => !base.includes(rank));
        if (free.length === 0) continue;

        // Siblings must take the shelves it leaves, else a short value reads as a band.
        const siblings = inRun.filter((item) => valueOf(item.skuId, attr) !== value);
        const siblingShelves = new Set(
          siblings.flatMap((item) => baseSpans(item.skuId, run.run)).map((span) => span.shelf),
        );
        if (!free.every((rank) => siblingShelves.has(rank))) continue;

        // Gates below test "owns these whole bays" on occupied width, not extent.
        const foreignSpans = siblings.flatMap((item) => baseSpans(item.skuId, run.run));
        const everything = inRun.flatMap((item) => baseSpans(item.skuId, run.run));
        const xLo = Math.min(...mine.map((span) => span.lo));
        const xHi = Math.max(...mine.map((span) => span.hi));
        const extent = xHi - xLo;
        if (extent <= 0) continue;

        const solid = base.every(
          (shelf) =>
            coveredCm(
              foreignSpans.filter((span) => span.shelf === shelf),
              xLo,
              xHi,
            ) /
              extent <=
            foreignFrac,
        );
        if (!solid) continue;

        const stacked = free.every(
          (shelf) =>
            coveredCm(
              everything.filter((span) => span.shelf === shelf),
              xLo,
              xHi,
            ) /
              extent >=
            stackFillFrac,
        );
        if (!stacked) continue;

        const bayAligned =
          run.bayWidthCm > 0 &&
          base.every((shelf) => {
            const rowMine = mine.filter((span) => span.shelf === shelf);
            for (let bay = 0; bay < run.bays; bay += 1) {
              const lo = bay * run.bayWidthCm;
              if (coveredCm(rowMine, lo, lo + run.bayWidthCm) / run.bayWidthCm >= bayFillFrac) {
                return true;
              }
            }
            return false;
          });
        if (!bayAligned) continue;

        const pooledAway = report.pooled.some(
          (node) => node.run === run.run && node.level === attr && node.values.includes(value),
        );
        if (pooledAway) continue;

        const carrier = carrierFor(liveIndex);
        // Naming V on a shelf empties that shelf's allow-list for its siblings.
        const siblingValues = new Set(siblings.map((item) => valueOf(item.skuId, attr)));
        siblingValues.delete("");
        findings.push({
          id: `vertical-band:${run.run}:${attr}:${value}`,
          kind: "vertical-band",
          run: run.run,
          attr,
          value,
          skuIds: own.map((item) => item.skuId),
          carrier,
          siblingEvictions:
            carrier.kind === "restriction-row" ? siblingValues.size * base.length : 0,
          aliases: [],
          ambiguous: false,
          detail:
            `"${value}" is a value of "${attr}", a VERTICAL level, but it owns whole bays ` +
            `on shelves ${base.join(", ")} only, with siblings stacked beneath it. A ` +
            "vertical level's values get full-height windows, so no value order reproduces this.",
          evidence: {
            baseShelves: base,
            builtShelves: sortedUnique(own.flatMap((item) => builtShelves(item.skuId))),
            notes: [
              `siblings hold shelves ${free.join(", ")}`,
              carrier.kind === "restriction-row"
                ? `a row costs ${siblingValues.size * base.length} sibling (value, shelf) exclusions — CAN_HAVE is not weaker than MUST_HAVE_ONLY here`
                : `carried by a mudmap ${carrier.kind === "mudmap" ? carrier.node : "node"}, not a restriction row: the row reaches CDT0 only`,
              `"${attr}" is banding rather than columning here; making it the HORIZONTAL level expresses this shape directly, but a role change rebuilds the whole plan`,
            ],
          },
        });
      }
    }

    // C2: MIX ASYMMETRY, not construction, breaks a cross-window band —
    // measured by whether each window's rebuild lands on the band's shelves.
    const hLevel = [...roleOf].find(([, role]) => role === "horizontal")?.[0];
    if (hLevel) {
      const liveIndex = liveIndexOf.get(hLevel) ?? 2;
      const windows = report.windows.filter((win) => win.run === run.run);
      const valuesSeen = new Set(inRun.map((item) => valueOf(item.skuId, hLevel)));
      for (const value of [...valuesSeen]) {
        if (isPlaceholderValue(value)) valuesSeen.delete(value);
      }

      for (const value of valuesSeen) {
        const perWindow = windows
          .map((win) => ({
            win,
            skuIds: win.skuIds.filter((skuId) => valueOf(skuId, hLevel) === value),
          }))
          .filter((entry) => entry.skuIds.length > 0);
        if (perWindow.length < 2) continue;

        const rows = perWindow.map((entry) => ({
          path: entry.win.path.join(PATH_SEPARATOR),
          skuIds: entry.skuIds,
          base: sortedUnique(
            entry.skuIds.flatMap((skuId) => baseSpans(skuId, run.run).map((span) => span.shelf)),
          ),
          built: sortedUnique(entry.skuIds.flatMap((skuId) => builtShelves(skuId))),
        }));
        // One band: contiguous across windows; windows need not fill the SAME shelves.
        const band = sortedUnique(rows.flatMap((row) => row.base));
        if (band.length === 0 || band.length >= shelfRanks.length) continue;
        if (!isContiguous(band)) continue;
        // Spilling outside the band is the signature of mix asymmetry.
        const spilled = rows.filter((row) => row.built.some((shelf) => !band.includes(shelf)));
        if (spilled.length === 0) continue;

        const carrier = carrierFor(liveIndex);
        const skuIds = rows.flatMap((row) => row.skuIds);
        findings.push({
          id: `horizontal-band:${run.run}:${hLevel}:${value}`,
          kind: "horizontal-band",
          run: run.run,
          attr: hLevel,
          value,
          skuIds,
          carrier,
          siblingEvictions: 0,
          aliases: [],
          ambiguous: false,
          detail:
            `"${value}" holds shelves ${band.join(", ")} across ${rows.length} sibling windows, ` +
            `but ${spilled.length} of them rebuild it outside that band — their assortment ` +
            "mixes differ, and nothing coordinates the allocation between windows.",
          evidence: {
            baseShelves: band,
            perWindow: rows.map((row) => ({ path: row.path, base: row.base, built: row.built })),
            notes: [
              carrier.kind === "mudmap"
                ? `carried by a mudmap ${carrier.node}`
                : "no carrier reaches this level",
              "no restriction row reaches this level as the tree stands; promotionRemedyFor() computes the CDT0 split that yields one, with its row and its eviction cost",
            ],
          },
        });
      }
    }

    // C4: buildFromCdt sorts by kg only WITHIN a band, so weight never crosses
    // a band boundary; band order is the horizontal level's priority order.
    const surfaceOf = new Map(run.shelves.map((shelf) => [shelf.rank, shelf.yCm]));
    const aboveLine = (shelf: number) => (surfaceOf.get(shelf) ?? 0) >= FT4_CM;
    const heavy = inRun.filter((item) => (item.weightKg ?? 0) > heavyKg);
    if (heavy.length > 0) {
      const offenders = heavy.filter((item) =>
        builtShelves(item.skuId).some((shelf) => aboveLine(shelf)),
      );
      if (offenders.length > 0) {
        // Spec §9: only a band across the top can break the rule.
        const banded =
          hLevel &&
          offenders.every((item) => {
            const shelves = sortedUnique(
              inRun
                .filter((sibling) => valueOf(sibling.skuId, hLevel) === valueOf(item.skuId, hLevel))
                .flatMap((sibling) => builtShelves(sibling.skuId)),
            );
            return shelves.length > 0 && shelves.length < shelfRanks.length;
          });
        const carrier: Carrier = banded ? { kind: "cdt-edit" } : { kind: "none" };
        const baseOffenders = heavy.filter((item) =>
          baseSpans(item.skuId, run.run).some((span) => aboveLine(span.shelf)),
        );
        findings.push({
          id: `heavy-above-4ft:${run.run}`,
          kind: "heavy-above-4ft",
          run: run.run,
          attr: hLevel ?? "",
          value: "",
          skuIds: offenders.map((item) => item.skuId),
          carrier,
          siblingEvictions: 0,
          aliases: [],
          ambiguous: false,
          detail:
            `${offenders.length} of ${heavy.length} packs over ${(options.heavyLb ?? 10).toFixed(0)} lb ` +
            `rebuild at or above ${FT4_CM} cm. ` +
            (banded
              ? `They sit in a band, so re-declaring "${hLevel}"'s value order moves them down — that costs nothing and pins nothing.`
              : "They sit in a full-height block, so no CDT edit moves them: this needs the post-layout heavy-below pass."),
          evidence: {
            notes: [
              `base pog: ${baseOffenders.length} of ${heavy.length} above the line`,
              `rebuild: ${offenders.length} of ${heavy.length} above the line`,
              "a rebuild can be shelf-exact and still breach this — band order is the horizontal level's, not weight's",
            ],
          },
        });
      }
    }
  }

  // C3: an ANSWERED pooling is excluded. Pooling cascades, so chain records —
  // one whose values contain an earlier chain's continues it, and only the last
  // link is reported.
  const structural = (reasons: readonly CdtPoolReason[]) =>
    reasons.filter((reason) => reason !== "answered-pooled");
  const chains: { key: string; values: Set<string>; node: (typeof report.pooled)[number] }[] = [];
  for (const node of report.pooled) {
    if (structural(node.reasons).length === 0) continue;
    const key = `${node.run}|${node.level}`;
    const chain = chains.find(
      (candidate) =>
        candidate.key === key &&
        [...candidate.values].every((value) => node.values.includes(value)),
    );
    if (chain) {
      chain.values = new Set(node.values);
      chain.node = node;
    } else {
      chains.push({ key, values: new Set(node.values), node });
    }
  }

  // Mutually exclusive: too few SKUs can never be a column at any setting.
  const thin = { tooNarrow: 0, tooFewSkus: 0 };
  for (const { node } of chains) {
    if (node.skuCount < node.shelfCount) thin.tooFewSkus += 1;
    else if (node.sliceCm < node.columnFloorCm) thin.tooNarrow += 1;
  }

  for (const { node } of chains) {
    const label = node.values.join(" + ");
    findings.push({
      id: `thin-column:${node.run}:${node.level}:${label}`,
      kind: "thin-column",
      run: node.run,
      attr: node.level,
      value: label,
      skuIds: node.skuIds,
      carrier: { kind: "cdt-edit" },
      siblingEvictions: 0,
      aliases: [],
      ambiguous: false,
      detail:
        node.skuCount < node.shelfCount
          ? `"${label}" has ${node.skuCount} SKUs for ${node.shelfCount} shelves, so it can never be a full-height column on this fixture at any setting — merging it into "${node.mergedInto.join(" + ")}" in the tree is the only settlement.`
          : `"${label}" earns ${node.sliceCm} cm but needs ${node.columnFloorCm} cm to hold a column of its widest pack — a strip, not a block. Merging it into "${node.mergedInto.join(" + ")}" removes the question.`,
      evidence: {
        notes: [
          `slice ${node.sliceCm} cm vs column floor ${node.columnFloorCm} cm`,
          `${node.skuCount} SKUs vs ${node.shelfCount} shelves`,
          `pooled into ${node.mergedInto.join(" + ")}`,
        ],
      },
    });
  }

  // C5 aliasing: identical partitions make attribution undecidable from
  // geometry, so report ONE finding, at the shallowest level.
  const banded = findings.filter(
    (finding) => finding.kind === "vertical-band" || finding.kind === "horizontal-band",
  );
  const byMembers = new Map<string, ExpressibilityFinding[]>();
  for (const finding of banded) {
    const key = `${finding.run}|${[...finding.skuIds].sort().join(",")}`;
    byMembers.set(key, [...(byMembers.get(key) ?? []), finding]);
  }
  const superseded = new Set<string>();
  for (const group of byMembers.values()) {
    if (group.length < 2) continue;
    const ranked = [...group].sort(
      (a, b) => (liveIndexOf.get(a.attr) ?? 0) - (liveIndexOf.get(b.attr) ?? 0),
    );
    const [primary, ...rest] = ranked;
    primary.aliases = rest.map((finding) => ({ attr: finding.attr, value: finding.value }));
    primary.ambiguous = true;
    for (const finding of rest) superseded.add(finding.id);
  }

  return {
    findings: findings.filter((finding) => !superseded.has(finding.id)),
    thin,
    weightUnavailable: items.every((item) => item.weightKg === undefined),
  };
};
